import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound, Smartphone, ShieldCheck } from 'lucide-react'
import { DateTime } from '@/components/ui/DateTime'
import type { Device } from '../api'

const ICON = { passkey: KeyRound, totp: Smartphone, app_key: ShieldCheck } as const

/**
 * One sign-in device: what it is, how it was enrolled, when it last signed in. Shared by "My
 * devices" and the administrator's list, which adds who and why for a revoked one.
 */
export function DeviceRow({ device, action, showAudit = false }: { device: Device; action?: ReactNode; showAudit?: boolean }) {
    const { t } = useTranslation()
    const Icon = ICON[device.kind] ?? KeyRound
    const revoked = device.status === 'revoked'
    return (
        <li
            className="flex items-start gap-3 rounded-md border bg-card p-3 text-sm"
            data-testid="device" data-device-id={device.id} data-status={device.status}
        >
            <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${revoked ? 'text-muted-foreground' : 'text-primary'}`} aria-hidden />
            <div className="min-w-0 flex-1">
                <div className={`font-medium ${revoked ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{device.name}</div>
                <div className="text-xs text-muted-foreground">
                    {t(`devices.kind.${device.kind}`)}
                    {device.makeModel ? ` · ${device.makeModel}` : ''}
                    {device.os ? ` · ${device.os}` : ''}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                    {t(`devices.via.${device.enrolledVia}`, { defaultValue: device.enrolledVia })}
                    {device.enrolledAt && <> · <DateTime date={device.enrolledAt} format="date" /></>}
                </div>
                <div className="text-xs text-muted-foreground" data-testid="device-last-used">
                    {device.lastUsedAt
                        ? <>{t('devices.lastUsed')} <DateTime date={device.lastUsedAt} format="relative" />{showAudit && device.lastUsedIp ? ` · ${device.lastUsedIp}` : ''}</>
                        : t('devices.neverUsed')}
                </div>
                {revoked && (
                    <div className="mt-1 text-xs text-destructive" data-testid="device-revoked">
                        {t('devices.revoked')}
                        {device.revokedAt && <> · <DateTime date={device.revokedAt} format="datetime" /></>}
                        {showAudit && device.revokeReason ? ` · ${device.revokeReason}` : ''}
                    </div>
                )}
            </div>
            {action}
        </li>
    )
}
